import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Mail, Phone, Lock, UserCheck, AlertCircle, Building, GraduationCap } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import FileUpload from '../../components/Common/FileUpload';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { validateEmail, validatePhone } from '../../utils/validation';

const AddStudent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [collegeDegrees, setCollegeDegrees] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    rollNumber: '',
    department: '',
    degree: '',
    idProof: '',
    certificates: [],
    dateOfBirth: '',
    gender: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const fetchCollegeDegrees = useCallback(async () => {
    try {
      const { data } = await api.get('/college-degrees');
      if (data.success) {
        setCollegeDegrees(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching college degrees:', error);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const { data } = await api.get('/departments');
      if (data.success) {
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchCollegeDegrees();
  }, [fetchDepartments, fetchCollegeDegrees]);

  const validateField = (name, value) => {
    switch (name) {
      case 'fullName':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 3) return 'Name must be at least 3 characters';
        return '';

      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value.trim())) return 'Please enter a valid email address';
        return '';

      case 'mobile':
        if (!value) return 'Mobile number is required';
        if (!validatePhone(value)) return 'Mobile number must be exactly 10 digits';
        return '';

      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';

      case 'rollNumber':
        if (!value.trim()) return 'Roll number is required';
        return '';

      case 'department':
        if (!value) return 'Department is required';
        return '';

      case 'degree':
        if (!value) return 'Degree is required';
        return '';

      case 'dateOfBirth':
        if (!value) return 'Date of birth is required';
        return '';

      case 'gender':
        if (!value) return 'Gender is required';
        return '';

      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For mobile, only allow numbers
    if (name === 'mobile') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData({
        ...formData,
        [name]: numericValue.slice(0, 10),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, name === 'mobile' ? value.replace(/[^0-9]/g, '').slice(0, 10) : value);
      setErrors({
        ...errors,
        [name]: error,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });

    const error = validateField(name, value);
    setErrors({
      ...errors,
      [name]: error,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    ['fullName', 'email', 'mobile', 'password', 'rollNumber', 'department', 'degree', 'dateOfBirth', 'gender'].forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    setTouched({
      fullName: true,
      email: true,
      mobile: true,
      password: true,
      rollNumber: true,
      department: true,
      degree: true,
      dateOfBirth: true,
      gender: true,
    });

    if (!formData.idProof) {
      toast.error('ID Proof document is strongly recommended');
      // we won't strictly block form submission if they dismiss the toast, or maybe we do? Let's not strictly block just in case, but let them know.
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!validateForm()) {
      toast.error('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const studentData = {
        ...formData,
        role: 'student',
        college: user.college,
        status: 'active',
      };

      const { data } = await api.post('/users', studentData);

      if (data.success) {
        toast.success('Student added successfully!');
        navigate('/college/students');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add student';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/college/students')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Add Student</h1>
          <p className="text-gray-600 mt-1">Add a new student to your institution</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <Input
                label="Full Name"
                name="fullName"
                type="text"
                icon={User}
                value={formData.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="Enter student's full name"
                className={errors.fullName && touched.fullName ? 'border-red-500' : ''}
                maxLength={100}
                autoComplete="off"
              />
              {errors.fullName && touched.fullName && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.fullName}
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <Input
                label="Email Address"
                name="email"
                type="email"
                icon={Mail}
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="student@example.com"
                className={errors.email && touched.email ? 'border-red-500' : ''}
                maxLength={255}
                autoComplete="off"
              />
              {errors.email && touched.email && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email}
                </div>
              )}
            </div>

            {/* Mobile */}
            <div>
              <Input
                label="Mobile Number"
                name="mobile"
                type="tel"
                icon={Phone}
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="1234567890"
                maxLength="10"
                className={errors.mobile && touched.mobile ? 'border-red-500' : ''}
                autoComplete="off"
              />
              {errors.mobile && touched.mobile && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.mobile}
                </div>
              )}
              {formData.mobile && !errors.mobile && (
                <div className="mt-1 text-sm text-green-600">
                  ✓ Valid mobile number
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <Input
                label="Password"
                name="password"
                type="password"
                icon={Lock}
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="Create a password"
                className={errors.password && touched.password ? 'border-red-500' : ''}
                maxLength={255}
                autoComplete="new-password"
              />
              {errors.password && touched.password && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </div>
              )}
              {formData.password && !errors.password && formData.password.length >= 6 && (
                <div className="mt-1 text-sm text-green-600">
                  ✓ Strong password
                </div>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <Input
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={errors.dateOfBirth && touched.dateOfBirth ? 'border-red-500' : ''}
              />
              {errors.dateOfBirth && touched.dateOfBirth && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.dateOfBirth}
                </div>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors.gender && touched.gender ? 'border-red-500' : 'border-gray-300'}`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && touched.gender && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.gender}
                </div>
              )}
            </div>

            {/* Roll Number */}
            <div>
              <Input
                label="Roll Number"
                name="rollNumber"
                type="text"
                value={formData.rollNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="e.g., 2024001"
                className={errors.rollNumber && touched.rollNumber ? 'border-red-500' : ''}
                maxLength={100}
                autoComplete="off"
              />
              {errors.rollNumber && touched.rollNumber && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.rollNumber}
                </div>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              {departments.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No departments found. Please add departments first.
                </p>
              )}
            </div>



            {/* Cascading Degree Selection */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
              <h3 className="md:col-span-2 text-sm font-semibold text-gray-900 mb-2">Degree / Program Configuration</h3>

              {/* Level Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  1. Degree Level
                </label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <select
                    value={selectedLevel}
                    onChange={(e) => {
                      setSelectedLevel(e.target.value);
                      setSelectedDomain('');
                      setFormData(prev => ({ ...prev, degree: '' }));
                    }}
                    className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="">Select Level</option>
                    {[...new Set(collegeDegrees.map(d => d.level))].sort().map(lvl => (
                      <option key={lvl} value={lvl}>{lvl}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Domain Selector */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${!selectedLevel ? 'text-gray-400' : 'text-gray-700'}`}>
                  2. Academic Domain
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <select
                    value={selectedDomain}
                    onChange={(e) => {
                      setSelectedDomain(e.target.value);
                      setFormData(prev => ({ ...prev, degree: '' }));
                    }}
                    disabled={!selectedLevel}
                    className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">Select Domain</option>
                    {[...new Set(collegeDegrees.filter(d => d.level === selectedLevel).map(d => d.domain))].sort().map(dom => (
                      <option key={dom} value={dom}>{dom}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Specialization Selector */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${!selectedDomain ? 'text-gray-400' : 'text-gray-700'}`}>
                  3. Specialization
                </label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <select
                    name="degree"
                    value={formData.degree}
                    onChange={handleChange}
                    required
                    disabled={!selectedDomain}
                    className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">Select a specialization</option>
                    {collegeDegrees
                      .filter(d => d.level === selectedLevel && d.domain === selectedDomain)
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                      .map((deg) => (
                        <option key={deg._id} value={deg._id}>
                          {deg.fullName} ({deg.specialization})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>



            {/* Document Uploads section */}
            <div className="md:col-span-2 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Required Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FileUpload
                    type="image"
                    label="Upload ID Proof (Image only - JPG/PNG)"
                    onUploadComplete={(fileData) => setFormData(prev => ({ ...prev, idProof: fileData.url }))}
                  />
                  {formData.idProof && <p className="text-xs text-green-600 mt-1">ID Proof attached successfully.</p>}
                </div>
                <div>
                  <FileUpload
                    type="image"
                    label="Upload Certificates (Image only - JPG/PNG)"
                    onUploadComplete={(fileData) => setFormData(prev => ({ ...prev, certificates: [...prev.certificates, fileData.url] }))}
                  />
                  {formData.certificates.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">{formData.certificates.length} certificate(s) attached.</p>
                  )}
                </div>
              </div>
            </div>

          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/college/students')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Adding Student...' : 'Add Student'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddStudent;
